import { showsReasoning } from '@marva/shared/decision-receipt'
import { LEDGER_CLASS_CHIPS } from '@marva/shared/ledger-vocabulary'
import type { LedgerItem } from '../../services/api'
import { outcomeLabel, receiptOf, type DisplayActivity } from '../../lib/activityNarrative'
import { isMerchantAction, isReplyDetail } from '../decision/StoryDrawer'

/**
 * The Ledger's line logic that is not layout (MAR-316, review findings 03/04/07): which status a
 * row states, and which stretch of the day it falls in. Pure, so it is tested without a screen.
 */

// ── The status chip (finding 04) ─────────────────────────────────────────────────────────────

/**
 * What a line says went out when it simply went out. "Sent" is the default, so on every row it is
 * noise: a row states a status only when it is NOT one of these.
 */
const DEFAULT_SENT_LABELS = new Set(['Sent', 'Delivered'])

/**
 * The classes with no server chip that still must not read as a plain send (the vocabulary
 * deliberately gives the failures no chip — `chipForClass`). Said here in the merchant's words.
 */
const NO_CHIP_CLASS_WORDS: Record<string, { label: string; tone: RowStatus['tone'] }> = {
  execution_failed: { label: 'Didn’t go through', tone: 'danger' },
  decision_failed: { label: 'Couldn’t decide', tone: 'danger' },
  help_withdrawn: { label: 'Withdrawn', tone: 'neutral' },
  // A pre-D2 skip/defer with no recorded provenance: nothing went out, and the row cannot say
  // whether that was her judgment or a limit — so it says only what is known, never "chose not to".
  legacy_ambiguous: { label: 'Not sent', tone: 'neutral' },
}

export type RowStatus = {
  label: string
  tone: 'neutral' | 'gold' | 'sage' | 'danger'
  /** "She chose not to" carries its own marker — the decisions a merchant most wants to check. */
  choseNot: boolean
}

/** The chip a "She chose not to" line carries (the vocabulary's own copy, never restated). */
export const CHOSE_NOT_CHIP: string | undefined = LEDGER_CLASS_CHIPS.wait

/**
 * The one status a row states, or null when it is the default send. Reads, in order: the server's
 * class chip (a campaign's multi-outcome chip included), then the classes whose chip is
 * deliberately null, then the row's stored outcome — never "Sent"/"Delivered".
 */
export function rowStatus(item: LedgerItem, gold: (chip: string) => boolean): RowStatus | null {
  const chip = item.chip?.trim()
  if (chip && !DEFAULT_SENT_LABELS.has(chip)) {
    if (chip === CHOSE_NOT_CHIP) return { label: chip, tone: 'sage', choseNot: true }
    return { label: chip, tone: gold(chip) ? 'gold' : 'neutral', choseNot: false }
  }
  if (!item.unreadable) {
    const cls = receiptOf(item as DisplayActivity)?.class
    const word = cls ? NO_CHIP_CLASS_WORDS[cls] : undefined
    if (word) return { label: word.label, tone: word.tone, choseNot: false }
  }
  const out = outcomeLabel(item.outcome)
  if (out && !DEFAULT_SENT_LABELS.has(out)) {
    const converted = item.outcome === 'conversion' || item.outcome === 'converted' || item.outcome === 'purchased'
    return { label: out, tone: converted ? 'sage' : 'neutral', choseNot: false }
  }
  return null
}

// ── The why ──────────────────────────────────────────────────────────────────────────────────

/**
 * The row's one-line "why" (MAR-246): `item.why`, and nothing else — Marva's own sentence, or,
 * for a row without one, the server's fallback composed from her recorded judgment
 * (`fallbackLedgerWhy`, MAR-269). The raw reasoning is never a row's why — not `reasoning`, not
 * `description` — it is her
 * working, in the drawer, behind a toggle. Gated again by the shared `showsReasoning` rule.
 */
export function ledgerWhy(item: LedgerItem): string | undefined {
  // A reply line (MAR-110) is not a judgment: its headline carries the content. Nor is the
  // merchant's own applied change (MAR-175): it was their call, so there is no why to give.
  if (isReplyDetail(item) || isMerchantAction(item)) return undefined
  const receipt = receiptOf(item as DisplayActivity)
  if (receipt && !showsReasoning(receipt)) return undefined
  const text = item.why?.trim()
  return text && text !== item.headline?.trim() ? text : undefined
}

// ── The window (finding 07) ──────────────────────────────────────────────────────────────────

export const ATTRIBUTION_WINDOWS = ['24h', '7d', '30d', '365d'] as const
export type AttributionWindow = (typeof ATTRIBUTION_WINDOWS)[number]

/** How each window reads in a sentence ("over the last 7 days"). */
export const WINDOW_WORDS: Record<AttributionWindow, string> = {
  '24h': 'the last 24 hours',
  '7d': 'the last 7 days',
  '30d': 'the last 30 days',
  '365d': 'the last year',
}

/** Each window's length — the list's lower bound is `now` minus this. */
const WINDOW_MS: Record<AttributionWindow, number> = {
  '24h': 24 * 3_600_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '365d': 365 * 86_400_000,
}

/**
 * The ledger route's `from` for a window (an inclusive ISO bound; there is no `to` — the window
 * runs to now). Fixed once per read: every "Show more" for that read sends the SAME bound, because
 * the route refuses a cursor that falls outside the range it was asked for.
 */
export function windowFrom(w: AttributionWindow, now: Date): string {
  return new Date(now.getTime() - WINDOW_MS[w]).toISOString()
}

/**
 * The list's section heading. Only the 24-hour window is "the night"; any longer window names
 * itself, so a week's decisions are never headed as one night. True because the list is BOUNDED
 * to the window (`windowFrom`).
 */
export function listHeading(w: AttributionWindow): string {
  if (w === '24h') return 'The night'
  const words = WINDOW_WORDS[w]
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// ── Day parts (finding 07) ───────────────────────────────────────────────────────────────────

/**
 * The timezone the Ledger reads days in. The workspace object the client holds carries no
 * timezone today (`Workspace` in stores/workspaceStore.ts; the only stored one is inside
 * engagement rules, a separate read), so this is the BROWSER's timezone — the merchant's own
 * clock, which is what "this morning" means to the person reading it.
 */
export function ledgerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

type LocalParts = { y: number; m: number; d: number; hour: number }

function localParts(date: Date, timeZone?: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  return { y: get('year'), m: get('month'), d: get('day'), hour: get('hour') }
}

/** A calendar day as a comparable number of days (timezone already applied). */
function dayNumber(p: LocalParts): number {
  return Math.round(Date.UTC(p.y, p.m - 1, p.d) / 86_400_000)
}

/** The hour from which last evening counts as "Overnight" (21:00 the day before). */
const OVERNIGHT_FROM_HOUR = 21
/** Today's hours before this are "Overnight". */
const MORNING_FROM_HOUR = 6

export type DayPart = { key: string; label: string }

/**
 * Which stretch a moment falls in, read in `timeZone`, relative to `now`:
 * Overnight (yesterday from 21:00 to today 06:00) · This morning · This afternoon · This
 * evening — and for anything older, its date ("Fri 23 Oct").
 */
export function dayPartOf(ts: string, now: Date, timeZone?: string): DayPart | null {
  const d = new Date(ts)
  if (isNaN(d.getTime())) return null
  const at = localParts(d, timeZone)
  const today = dayNumber(localParts(now, timeZone))
  const day = dayNumber(at)
  if (day === today) {
    if (at.hour < MORNING_FROM_HOUR) return { key: 'overnight', label: 'Overnight' }
    if (at.hour < 12) return { key: 'morning', label: 'This morning' }
    if (at.hour < 18) return { key: 'afternoon', label: 'This afternoon' }
    return { key: 'evening', label: 'This evening' }
  }
  if (day === today - 1 && at.hour >= OVERNIGHT_FROM_HOUR) return { key: 'overnight', label: 'Overnight' }
  const label = d.toLocaleDateString('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short' })
  return { key: `day-${day}`, label }
}

export type DayGroup<T> = { key: string; label: string | null; items: T[] }

/**
 * The lines, newest first, cut into runs by day part. Order is never changed: a run is
 * consecutive lines in the same part, so the list reads exactly as the server ordered it. A line
 * with no readable time joins the run before it (or opens an unlabelled one).
 */
export function groupByDayPart<T extends { timestamp: string }>(items: T[], now: Date, timeZone?: string): DayGroup<T>[] {
  const groups: DayGroup<T>[] = []
  for (const item of items) {
    const part = dayPartOf(item.timestamp, now, timeZone)
    const last = groups[groups.length - 1]
    if (last && (!part || last.key === part.key)) {
      last.items.push(item)
    } else {
      groups.push({ key: part ? part.key : `none-${groups.length}`, label: part ? part.label : null, items: [item] })
    }
  }
  return groups
}
